<template>
  <div class="map-view">
    <!-- Two different situations, and telling them apart is the whole point:
         with no GeoData field in the table there is nothing to choose, so
         "choose a field above" sends the user to an empty dropdown. That case
         gets the way out instead -- create the field here. -->
    <div v-if="!geoDataField" class="map-view__empty">
      <div class="map-view__empty-inner">
        <!-- The same glyph MapViewType.getIconClass returns, so the empty state
             is recognisably this view rather than a generic placeholder.
             `iconoir-map-pin` does not exist in the icon set and rendered as
             the font's .notdef box -- a grey square. -->
        <i class="map-view__empty-icon iconoir-map" aria-hidden="true"></i>
        <h3 class="map-view__empty-title">
          {{
            hasGeoDataFields
              ? $t('mapView.chooseFieldTitle')
              : $t('mapView.noFieldTitle')
          }}
        </h3>
        <p class="map-view__empty-text">
          {{
            hasGeoDataFields
              ? $t('mapView.chooseFieldText')
              : $t('mapView.noFieldText')
          }}
        </p>
        <!-- Anchored on the event target rather than a ref: Context positions
             itself with `getBoundingClientRect`, so it needs the element, and
             Button is a component whose `$el` is an implementation detail. -->
        <Button
          v-if="!hasGeoDataFields && !readOnly"
          icon="iconoir-plus"
          @click="$refs.createFieldContext.toggle($event.currentTarget)"
        >
          {{ $t('mapView.createGeoDataField') }}
        </Button>
        <CreateFieldContext
          ref="createFieldContext"
          :table="table"
          :view="view"
          :all-fields-in-table="fields"
          :database="database"
        ></CreateFieldContext>
      </div>
    </div>
    <LeafletMapView
      v-else
      class="map-view__map"
      :markers="markers"
      @marker-click="rowClick"
    ></LeafletMapView>
    <RowEditModal
      ref="rowEditModal"
      enable-navigation
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="true"
      :visible-fields="popupFields"
      :hidden-fields="hiddenFields"
      :rows="allRows"
      :read-only="
        readOnly ||
        (!$hasPermission(
          'database.table.update_row',
          table,
          database.workspace.id
        ) &&
          !$hasPermission(
            'database.table.view.update_row',
            view,
            database.workspace.id
          ))
      "
      :show-hidden-fields="showHiddenFieldsInRowModal"
      @hidden="$emit('selected-row', undefined)"
      @toggle-hidden-fields-visibility="
        showHiddenFieldsInRowModal = !showHiddenFieldsInRowModal
      "
      @update="updateValue"
      @order-fields="orderFields"
      @toggle-field-visibility="toggleFieldVisibility"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="showFieldCreated"
      @field-created-callback-done="afterFieldCreatedUpdateFieldOptions"
      @navigate-previous="$emit('navigate-previous', $event, activeSearchTerm)"
      @navigate-next="$emit('navigate-next', $event, activeSearchTerm)"
      @refresh-row="refreshRow"
    >
    </RowEditModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { notifyIf } from '@baserow/modules/core/utils/error'
import { escapeHtml } from '@baserow/modules/core/utils/string'
import {
  sortFieldsByOrderAndIdFunction,
  filterVisibleFieldsFunction,
  filterHiddenFieldsFunction,
} from '@baserow/modules/database/utils/view'
import LeafletMapView from '@baserow/modules/database/components/map/LeafletMapView'
import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import CreateFieldContext from '@baserow/modules/database/components/field/CreateFieldContext'
import viewHelpers from '@baserow/modules/database/mixins/viewHelpers'

export default {
  name: 'MapView',
  components: { LeafletMapView, RowEditModal, CreateFieldContext },
  mixins: [viewHelpers],
  props: {
    fields: {
      type: Array,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    database: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
    storePrefix: {
      type: String,
      required: true,
    },
  },
  emits: ['navigate-next', 'navigate-previous', 'refresh', 'selected-row'],
  data() {
    return {
      showHiddenFieldsInRowModal: false,
    }
  },
  computed: {
    ...mapGetters({
      row: 'rowModalNavigation/getRow',
    }),
    allRows() {
      return this.$store.getters[this.storePrefix + 'view/map/getRows']
    },
    fieldOptions() {
      return this.$store.getters[
        this.storePrefix + 'view/map/getAllFieldOptions'
      ]
    },
    geoDataField() {
      const fieldId = this.view.geo_data_field
      return this.fields.find((field) => field.id === fieldId) || null
    },
    /**
     * Whether the table has anything a map could plot at all. Distinguishes
     * "pick one of these" from "there are none to pick", which are the same
     * blank screen otherwise.
     */
    hasGeoDataFields() {
      return this.fields.some((field) => field.type === 'geo_data')
    },
    /**
     * The fields shown in the marker popup and in the row edit modal, mirroring
     * how Gallery decides `cardFields`: whatever field_options mark visible.
     */
    popupFields() {
      const fieldOptions = this.fieldOptions
      return this.fields
        .filter(filterVisibleFieldsFunction(fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(fieldOptions))
    },
    hiddenFields() {
      const fieldOptions = this.fieldOptions
      return this.fields
        .filter(filterHiddenFieldsFunction(fieldOptions))
        .sort(sortFieldsByOrderAndIdFunction(fieldOptions))
    },
    activeSearchTerm() {
      return this.$store.getters[
        `${this.storePrefix}view/map/getActiveSearchTerm`
      ]
    },
    /**
     * Maps rows into the shape LeafletMapView expects for its `markers` prop,
     * reading the position from the geo_data field and rendering the popup
     * content from whichever fields are visible in the field options.
     */
    markers() {
      const geoDataField = this.geoDataField
      if (!geoDataField) {
        return []
      }
      // `getRows` can contain `null` placeholders for buffered rows that
      // haven't been fetched from the backend yet, so those are skipped.
      return this.allRows
        .filter((row) => row !== null)
        .map((row) => this.rowToMarker(row, geoDataField))
        .filter((marker) => marker !== null)
    },
  },
  watch: {
    // Unlike Gallery/Grid, the map has no scroll-driven viewport of visible
    // rows -- every row with a position needs a marker up front, so the full
    // range is requested as soon as the row count is known (or changes).
    'allRows.length'(newLength) {
      this.fetchAllRows(newLength)
    },
    // Mirrors Gallery: opens/closes the row edit modal when deep-linked or
    // navigated to from outside this component (e.g. row modal navigation).
    row: {
      deep: true,
      handler(row, oldRow) {
        if (this.$refs.rowEditModal) {
          if (
            (oldRow === null && row !== null) ||
            (oldRow && row && oldRow.id !== row.id)
          ) {
            this.$refs.rowEditModal.show(row.id)
          } else if (oldRow !== null && row === null) {
            this.$refs.rowEditModal.hide(false)
          }
        }
      },
    },
  },
  mounted() {
    this.fetchAllRows(this.allRows.length)
    if (this.row !== null) {
      this.$refs.rowEditModal.show(this.row.id)
    }
  },
  methods: {
    fetchAllRows(count) {
      if (!count) {
        return
      }
      this.$store.dispatch(
        this.storePrefix + 'view/map/fetchMissingRowsInNewRange',
        { startIndex: 0, endIndex: count }
      )
    },
    rowToMarker(row, geoDataField) {
      const value = row[`field_${geoDataField.id}`]
      if (!value || value.lat == null || value.lng == null) {
        return null
      }
      return {
        id: row.id,
        lat: Number(value.lat),
        lng: Number(value.lng),
        popupHtml: this.buildPopupHtml(row),
      }
    },
    /**
     * Builds the popup's inner HTML from the visible field_options fields, the
     * same fields Gallery would show on a card. Values are escaped because
     * Leaflet's bindPopup renders this string as raw HTML.
     */
    buildPopupHtml(row) {
      const parts = this.popupFields.map((field) => {
        const fieldType = this.$registry.get('field', field.type)
        const value = row[`field_${field.id}`]
        const text = fieldType.toHumanReadableString(field, value)
        return (
          `<div class="map-view__popup-field">` +
          `<div class="map-view__popup-field-name">${escapeHtml(
            field.name
          )}</div>` +
          `<div class="map-view__popup-field-value">${escapeHtml(text)}</div>` +
          `</div>`
        )
      })
      return `<div class="map-view__popup">${parts.join('')}</div>`
    },
    rowClick(rowId) {
      this.$refs.rowEditModal.show(rowId)
      const row = this.allRows.find((r) => r.id === rowId)
      this.$emit('selected-row', row)
    },
    async updateValue({ field, row, value, oldValue }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/map/updateRowValue',
          {
            table: this.table,
            view: this.view,
            fields: this.fields,
            row,
            field,
            value,
            oldValue,
          }
        )
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    refreshRow(row) {
      if (this.refreshingRow) {
        return
      }
      this.refreshingRow = true

      this.$nextTick(async () => {
        try {
          await this.$store.dispatch(
            this.storePrefix + 'view/map/refreshRowFromBackend',
            { table: this.table, row }
          )
        } catch (error) {
          notifyIf(error, 'row')
        } finally {
          this.refreshingRow = false
        }
      })
    },
    showFieldCreated({ fetchNeeded, ...context }) {
      this.fieldCreated({ fetchNeeded, ...context })
      this.showHiddenFieldsInRowModal = true
    },
  },
}
</script>
